<template>
    <div v-if="isLoaded" class='demo-app'>
        <div class='demo-app-main'>
            <div v-if="peopleGroups.length" class="group-filter-card">
                <div class="gf-header">
                    <div class="gf-title"><i class="material-icons-round">groups</i> Filter by People Groups</div>
                    <button v-if="selectedGroupIds.length" type="button" class="gf-clear" @click="clearGroupFilters">
                        Clear filters
                    </button>
                </div>

                <div class="gf-section">
                    <div class="gf-label">People Groups <span class="gf-hint">Select one or more to show their assignments</span></div>
                    <div class="gf-chips">
                        <button v-for="group in peopleGroups" :key="group.id" type="button" class="gf-chip"
                            :class="{ active: isGroupSelected(group.id) }" :aria-pressed="isGroupSelected(group.id)"
                            @click="toggleGroup(group.id)">
                            <i v-if="isGroupSelected(group.id)" class="material-icons-round gf-check">check</i>
                            {{ group.name }}
                            <span class="gf-count">{{ (group.members || []).length }}</span>
                        </button>
                    </div>
                </div>

                <transition name="gf-fade">
                    <div v-if="selectedGroupIds.length" class="gf-section gf-members">
                        <div class="gf-label">
                            Group Members
                            <span class="gf-hint">
                                {{ selectedGroupMemberIds.length
                                    ? `${selectedGroupMemberIds.length} selected`
                                    : 'Showing everyone - pick members to narrow it down' }}
                            </span>
                        </div>
                        <div v-for="group in groupedMemberOptions" :key="group.id" class="gf-member-group">
                            <div class="gf-member-group-name">{{ group.name }}</div>
                            <div v-if="group.members.length" class="gf-chips">
                                <button v-for="member in group.members" :key="member.value" type="button"
                                    class="gf-chip gf-chip-member" :class="{ active: isMemberSelected(member.value) }"
                                    :aria-pressed="isMemberSelected(member.value)" @click="toggleMember(member.value)">
                                    <i v-if="isMemberSelected(member.value)" class="material-icons-round gf-check">check</i>
                                    {{ member.label }}
                                </button>
                            </div>
                            <small v-else class="text-secondary">No members in this group yet.</small>
                        </div>
                    </div>
                </transition>
                <small v-if="groupCalendarError" class="text-danger">{{ groupCalendarError }}</small>
            </div>
            <small v-else-if="groupCalendarError" class="text-danger">{{ groupCalendarError }}</small>
            <FullCalendar ref="calendarRef" class='demo-app-calendar' :options='calendarOptions' :dayMaxEvents="true"
                :dayMaxEventRows="false">
                <template v-slot:eventContent='arg'>
                    <i class="material-icons-round opacity-10 fs-5">{{ arg.event._def.extendedProps.statusname ==
                        "pending" ? "question_mark" : "check" }} </i>
                    <i>{{ convertTimeStampToTime(arg.event._def.extendedProps.startdtm) }} {{ arg.event._def.extendedProps.name }}</i>
                </template>
            </FullCalendar>
        </div>
    </div>

    <Modal v-if="isModalOpen" :modalHeading="modalHeading" :modalContent="modalContent" :iframeUrl="iframeUrl"
        refresh-on-close @close="closeModal">
    </Modal>

    <!-- Toast notification for past records -->
    <div class="position-fixed bottom-1 end-1 z-index-2">
        <material-snackbar
            v-if="showPastRecordToast"
            title="Past Record"
            date=""
            description="This record is in the past and cannot be updated."
            :icon="{ component: 'warning', color: 'white' }"
            color="warning"
            :close-handler="closePastRecordToast"
        />
    </div>

</template>

<script>
import Modal from '@/components/Modal/Modal.vue';
import MaterialSnackbar from '@/components/common/MaterialSnackbar.vue';
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { mapActions, mapGetters } from 'vuex/dist/vuex.cjs.js';
import { baseUrl } from "@/utils/BaseUrl.js";
import api from "@/config/api";
import { getLocationId } from "@/utils/SessionDataHandler";

export default ({
    name: 'calendar',
    components: {
        FullCalendar,
        Modal,
        MaterialSnackbar
    },
    data() {
        return {
            isModalOpen: false,
            modalHeading: "",
            modalContent: "",
            showPastRecordToast: false,
            selectedEventAiwgid: null,
            calendarOptions: {
                plugins: [
                    dayGridPlugin,
                    timeGridPlugin,
                    interactionPlugin
                ],
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                },
                initialView: 'dayGridMonth',
                initialEvents: [],
                editable: false,
                selectable: true,
                selectMirror: true,
                dayMaxEvents: true,
                weekends: true,
                // select: this.handleDateSelect,
                eventClick: this.handleEventClick.bind(this),
                eventsSet: this.handleEvents.bind(this),
                datesSet: this.handleDatesSet.bind(this)
            },
            currentEvents: [],
            isLoaded: false,
            calendarApi: null,
            visibleRange: null,
            peopleGroups: [],
            selectedGroupIds: [],
            selectedGroupMemberIds: [],
            groupCalendarError: ""
        }
    },
    computed: {
        ...mapGetters([
            "stCalendarData",
            "stSessionData"
        ]),
        groupedMemberOptions() {
            const selectedIds = new Set(this.selectedGroupIds.map(Number));
            return this.peopleGroups
                .filter(group => selectedIds.has(Number(group.id)))
                .map(group => ({
                    id: group.id,
                    name: group.name,
                    members: (group.members || []).map(member => ({
                        value: `${group.id}:${member.user_id}`,
                        label: member.name || member.email || `Person #${member.user_id}`
                    }))
                }));
        },
        groupMemberOptions() {
            return this.groupedMemberOptions.flatMap(group => group.members);
        }
    },
    methods: {
        ...mapActions([
            'getCalendar'
        ]),

        isGroupSelected(id) {
            return this.selectedGroupIds.includes(String(id));
        },
        isMemberSelected(value) {
            return this.selectedGroupMemberIds.includes(value);
        },
        async toggleGroup(id) {
            const key = String(id);
            this.selectedGroupIds = this.isGroupSelected(id)
                ? this.selectedGroupIds.filter(g => g !== key)
                : [...this.selectedGroupIds, key];
            await this.refreshGroupFilters();
        },
        async toggleMember(value) {
            this.selectedGroupMemberIds = this.isMemberSelected(value)
                ? this.selectedGroupMemberIds.filter(m => m !== value)
                : [...this.selectedGroupMemberIds, value];
            await this.refreshCalendarEvents();
        },
        async clearGroupFilters() {
            this.selectedGroupIds = [];
            this.selectedGroupMemberIds = [];
            await this.refreshCalendarEvents();
        },

        async handleDatesSet(info) {
            const startTimestamp = new Date(info.start).getTime() / 1000;
            const endTimestamp = new Date(info.end).getTime() / 1000;
            this.calendarApi = info.view.calendar;
            this.visibleRange = { start: startTimestamp, end: endTimestamp };

            try {
                await this.getCalendar({
                    personid: this.stSessionData.personId,
                    start: startTimestamp,
                    end: endTimestamp,
                });
                await this.refreshCalendarEvents();
                this.isLoaded = true;  // Set loading flag to true after data is fetched
            } catch (error) {
                console.error('Error in fetching calendar data:', error);
            }
        },

        async loadPeopleGroups() {
            try {
                const { data } = await api.get("/people-groups", {
                    params: { location_id: getLocationId() }
                });
                this.peopleGroups = data?.data || [];
            } catch (error) {
                this.groupCalendarError = "People Groups could not be loaded.";
                console.error("Failed to load calendar People Groups:", error);
            }
        },

        async refreshGroupFilters() {
            const availableMembers = new Set(this.groupMemberOptions.map(member => member.value));
            this.selectedGroupMemberIds = this.selectedGroupMemberIds.filter(id => availableMembers.has(id));
            await this.refreshCalendarEvents();
        },

        async refreshCalendarEvents() {
            if (!this.calendarApi || !this.visibleRange) return;
            try {
                const groupEvents = await this.loadGroupAssignmentEvents();
                const events = this.transformCalendarEvents(this.stCalendarData).concat(groupEvents);
                this.calendarApi.removeAllEvents();
                this.calendarApi.addEventSource(events);
                this.groupCalendarError = "";
            } catch (error) {
                this.groupCalendarError = "People Group assignments could not be loaded.";
                console.error("Failed to load People Group calendar assignments:", error);
                this.calendarApi.removeAllEvents();
                this.calendarApi.addEventSource(this.transformCalendarEvents(this.stCalendarData));
            }
        },

        async loadGroupAssignmentEvents() {
            const groupIds = this.selectedGroupIds.map(Number).filter(id => id > 0);
            if (groupIds.length === 0) return [];

            const memberFilters = new Set(this.selectedGroupMemberIds);
            const results = await Promise.all(groupIds.map(groupId => api.get(`/people-groups/${groupId}`, {
                params: {
                    start: this.visibleRange.start,
                    end: this.visibleRange.end
                }
            })));

            return results.flatMap(response => {
                const group = response.data?.data;
                return (response.data?.shared_assignments || [])
                    .flatMap(assignment => assignment.member_ids
                        .filter(memberId => memberFilters.size === 0 || memberFilters.has(`${group.id}:${memberId}`))
                        .map(memberId => {
                            const member = group.members?.find(item => Number(item.user_id) === Number(memberId));
                            const memberName = member?.name
                                || assignment.member_names[assignment.member_ids.indexOf(memberId)]
                                || `Person #${memberId}`;
                            const name = `${memberName}: ${assignment.activity_name} - ${assignment.workgroup_name}`;
                            return {
                                id: `people-group-${group.id}-${assignment.ai_workgroup_id}-${memberId}`,
                                title: name,
                                start: assignment.start_datetime,
                                extendedProps: {
                                    name,
                                    description: `${memberName} is serving with ${assignment.workgroup_name}.`,
                                    startdtm: assignment.start_datetime,
                                    statusname: "confirmed",
                                    aiwgid: assignment.ai_workgroup_id,
                                    peopleGroupEvent: true,
                                    groupId: group.id,
                                    groupName: group.name,
                                    personId: Number(memberId),
                                    memberName,
                                }
                            };
                        }));
            });
        },

        handleWeekendsToggle() {
            this.calendarOptions.weekends = !this.calendarOptions.weekends // update a property
        },
        // handleDateSelect() {
        //     this.openModal()
        // },
        async handleEventClick(eventInfo) {
            if (eventInfo.event.extendedProps.peopleGroupEvent) {
                eventInfo.jsEvent.preventDefault();
                await this.openGroupMemberSchedule(eventInfo.event.extendedProps);
                return;
            }
            console.log("Full Event Object:", eventInfo.event);
            console.log("Extended Props:", eventInfo.event.extendedProps);
            const eventStart = eventInfo.event.start
                ? new Date(eventInfo.event.start)
                : new Date(eventInfo.event._def.extendedProps.startdtm);
            if (eventStart < new Date()) {
                eventInfo.jsEvent.preventDefault();
                this.showPastRecordToast = true;
                // Auto-hide toast after 5 seconds
                setTimeout(() => {
                    this.showPastRecordToast = false;
                }, 5000);
                return;
            }

            // Get the aiwgid from the clicked event
            const aiwgid = eventInfo.event._def.extendedProps.aiwgid;
            this.selectedEventAiwgid = aiwgid;

            eventInfo.jsEvent.preventDefault();
            this.openModal()
        },
        async openGroupMemberSchedule(props) {
            try {
                const { data } = await api.get(`/people-groups/${props.groupId}/members/${props.personId}/schedule-access`);
                if (!data?.can_view) {
                    throw new Error("You do not have permission to view this member's schedule.");
                }

                const params = new URLSearchParams({
                    personid: String(props.personId),
                    locationid: String(getLocationId()),
                    aiwgid_list: String(props.aiwgid),
                    show_chart: "0",
                });
                this.modalHeading = `${props.memberName}'s Schedule`;
                this.modalContent = "";
                this.iframeUrl = `${baseUrl}/MF/cs-mf-personschedule/?${params.toString()}`;
            } catch (error) {
                this.modalHeading = "Schedule unavailable";
                this.modalContent = `<p>${this.escapeHtml(error?.response?.data?.message || error.message || "You do not have permission to view this member's schedule.")}</p>`;
                this.iframeUrl = "";
            }
            this.isModalOpen = true;
        },
        handleEvents(events) {
            this.currentEvents = events
        },
        openModal() {
            this.isModalOpen = true;
            this.modalHeading = "My Assignments";
            let url = `${baseUrl}/MF/cs-mf-personschedule/?personid=${this.stSessionData.personId}&show_chart=0`;
            if (this.selectedEventAiwgid) {
                url += `&aiwgid_list=${this.selectedEventAiwgid}`;
            }
            this.iframeUrl = url;
        },
        closeModal() {
            this.isModalOpen = false;
            this.selectedEventAiwgid = null;
        },
        closePastRecordToast() {
            this.showPastRecordToast = false;
        },
        convertTimeStampToTime(timestamp) {
            const date = new Date(timestamp);
            return date.toLocaleString('en-US', {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
            });
        },
        escapeHtml(value) {
            return String(value || "").replace(/[&<>"']/g, character => ({
                "&": "&amp;",
                "<": "&lt;",
                ">": "&gt;",
                "\"": "&quot;",
                "'": "&#39;"
            }[character]));
        },
        transformCalendarEvents(apiEvents) {
            if (!Array.isArray(apiEvents)) {
                return [];
            }
            return apiEvents.map(event => ({
                id: event.id,
                title: event.name,
                start: event.startdtm,
                end: event.enddtm,
                extendedProps: {
                    name: event.name,
                    description: event.description,
                    note: event.note,
                    startdtm: event.startdtm,
                    enddtm: event.enddtm,
                    statusid: event.statusid,
                    statusname: event.statusid === 1 ? "confirmed" : event.statusid === 2 ? "declined" : "pending",
                    url: event.url,
                    aiwgid: event.aiwgid
                }
            }));
        }
    },
    async mounted() {
        this.isLoaded = false
        await this.loadPeopleGroups();
        const now = new Date();
        const startTimestamp = new Date(now.getFullYear(), now.getMonth(), 1).getTime() / 1000;
        const endTimestamp = new Date(now.getFullYear(), now.getMonth() + 1, 0).getTime() / 1000;

        this.getCalendar({
            personid: this.stSessionData.personId,
            start: startTimestamp,
            end: endTimestamp
        }).then(() => {
            const transformedEvents = this.transformCalendarEvents(this.stCalendarData);
            this.calendarOptions.initialEvents = transformedEvents;
            this.isLoaded = true;
        }).catch((error) => {
            console.log('Error in fetching Data', error);
        })

    },
})

</script>

<style lang='css' scoped>
i {
    text-wrap: wrap;
    padding: 2px;
    border-radius: 2px;
}

.fc-v-event .fc-event-main {
    font-size: 10px;
}

.demo-app {
    display: flex;
    min-height: 100%;
    font-family: Arial, Helvetica Neue, Helvetica, sans-serif;
    font-size: 14px;
}

.demo-app-main {
    flex-grow: 1;
    padding: 3em;
}

.group-filter-card {
    max-width: 1100px;
    margin: 0 auto 16px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 14px 18px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.gf-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.gf-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    color: #1e293b;
}

.gf-title i {
    font-size: 20px;
    color: #0e7490;
    padding: 0;
}

.gf-clear {
    border: 0;
    background: transparent;
    color: #0e7490;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
}

.gf-clear:hover {
    text-decoration: underline;
}

.gf-section + .gf-section {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #e2e8f0;
}

.gf-label {
    font-size: 0.8rem;
    font-weight: 700;
    color: #475569;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.gf-hint {
    margin-left: 8px;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    color: #94a3b8;
}

.gf-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.gf-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    color: #334155;
    border-radius: 999px;
    padding: 5px 14px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.15s ease;
}

.gf-chip:hover {
    border-color: #0e7490;
    background: #ecfeff;
}

.gf-chip.active {
    background: #0e7490;
    border-color: #0e7490;
    color: #fff;
    font-weight: 600;
}

.gf-chip-member.active {
    background: #16a34a;
    border-color: #16a34a;
}

.gf-chip .gf-check {
    font-size: 15px;
    padding: 0;
}

.gf-count {
    background: rgba(100, 116, 139, 0.18);
    border-radius: 999px;
    padding: 0 7px;
    font-size: 0.72rem;
}

.gf-chip.active .gf-count {
    background: rgba(255, 255, 255, 0.25);
}

.gf-members {
    max-height: 260px;
    overflow-y: auto;
}

.gf-member-group + .gf-member-group {
    margin-top: 10px;
}

.gf-member-group-name {
    font-size: 0.8rem;
    font-weight: 600;
    color: #0e7490;
    margin-bottom: 6px;
}

.gf-fade-enter-active,
.gf-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.gf-fade-enter-from,
.gf-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

.fc {
    /* the calendar root */
    max-width: 1100px;
    margin: 0 auto;
}
</style>
